import { useCopyToClipboard } from 'react-use'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { useHistory } from 'react-router-dom'
import { generatePath } from 'react-router-dom'
import { DOC_PATH } from 'paths'
import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PollSuggestionRequest } from './PollSuggestionRequest'
import { StreamContentData } from './StreamContentData'
import { AlmanacFile, SuggestionRequestStatus } from 'types'
import { showToast } from 'common/ToastMessage'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useGenerateContent } from 'components/Wizards/Wizard/useGenerateContent'
import { GeneratingContentToast } from 'components/GeneratingContentToast/GeneratingContentToast'
import { sendToApi } from 'api/client'
import { CaptionDisplay } from './components/CaptionDisplay'
import { ActionButtons } from './components/ActionButtons'
import { extractAllOptions } from './markdownUtils'
import { observable } from 'mobx'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { MessageType } from 'antd/lib/message'
import { CaptionOptionsList } from './CaptionOptionsList'

interface Props extends StepProps {
  file: AlmanacFile
  requestId: number
  setRequestId: (id: number) => void
  currentStep: number
  setCurrentStep: (step: number) => void
}

export const GeneratedContent = observer(
  multiStep(({ file, requestId, setRequestId, initialValues, goBack }: Props) => {
    const [, copyToClipboard] = useCopyToClipboard()
    const history = useHistory()
    const { drawersStore } = useRootStore()
    const { closeAllModals } = useModals()
    const [jobId, setJobId] = useState<string>()
    const [content, setContent] = useState<string>('')
    const [streaming, setStreaming] = useState<boolean>(false)
    const [hasError, setHasError] = useState<boolean>(false)
    const [selectedCopyId, setSelectedCopyId] = useState<number>(0)
    const [allOptions] = useState(() => observable.array<string>([]))
    const streamAbortController = useRef<AbortController>()
    const generatingToastRef = useRef<MessageType>()
    const channel = file.channel as ChannelType

    const clearAll = useCallback(() => {
      setContent('')
      allOptions.clear()
      setStreaming(false)
      setHasError(false)
      setSelectedCopyId(0)
      generatingToastRef.current?.()
    }, [allOptions])

    useEffect(() => {
      if (channel && content) {
        const options = extractAllOptions(content, channel)
        allOptions.replace(options)
      }
    }, [content, channel, allOptions])

    useEffect(() => {
      return () => {
        streamAbortController.current?.abort()
        generatingToastRef.current?.()
      }
    }, [])

    const displayedCaption = allOptions.length ? allOptions[selectedCopyId] : ''

    const handleAbort = useCallback(async () => {
      streamAbortController.current?.abort()
      if (file.id && file.main_layer_id && requestId) {
        await sendToApi<{}>(
          `/articles/${file.id}/layers/${file.main_layer_id}/ai_suggestion_requests/${requestId}/abort`,
          { method: 'POST' },
        )
      }
      clearAll()
      goBack()
    }, [file.id, file.main_layer_id, requestId, clearAll, goBack])

    const showGeneratingToast = () => {
      streamAbortController.current = new AbortController()
      generatingToastRef.current = showToast(<GeneratingContentToast onCancel={handleAbort} />, 0)
    }

    const handleStreaming = (newJobId: string) => {
      if (!streaming) {
        showGeneratingToast()
      }
      setJobId(newJobId)
      setStreaming(true)
    }

    const handleStreamComplete = (generatedContent: string | null, status?: SuggestionRequestStatus) => {
      setStreaming(false)
      setContent(generatedContent || '')
      setHasError(!generatedContent?.length || status === SuggestionRequestStatus.Failed)
      generatingToastRef.current?.()
    }

    const { generateContent: regenerate } = useGenerateContent({
      generationType: channel,
      articleId: file.id,
      layerId: file.main_layer_id,
    })

    const handleCopy = useCallback(() => {
      copyToClipboard(displayedCaption)
      showToastMessage('Caption copied to clipboard', 'success')
    }, [copyToClipboard, displayedCaption])

    const handleRegenerate = useCallback(async () => {
      clearAll()
      try {
        const result = await regenerate(initialValues)
        if ('node' in result) {
          setRequestId(Number(result.node.request_id))
        } else {
          setHasError(true)
        }
      } catch (error) {
        setHasError(true)
      }
    }, [initialValues, regenerate, clearAll, setRequestId])

    const handleOpenInWriter = useCallback(() => {
      drawersStore.closeAllDrawers()
      closeAllModals()
      history.push(generatePath(DOC_PATH, { slug: file.id }))
    }, [closeAllModals, drawersStore, file.id, history])

    return (
      <>
        {file.id && file.main_layer_id && (
          <PollSuggestionRequest
            id={requestId}
            articleId={file.id}
            layerId={file.main_layer_id}
            onStreaming={handleStreaming}
            onStreamComplete={handleStreamComplete}
          />
        )}
        {jobId && (
          <StreamContentData
            jobId={jobId}
            setStreamContent={setContent}
            signal={streamAbortController.current?.signal}
          />
        )}
        <MobileWizard.Content>
          <MobileWizard.Section>
            <MobileWizard.Title>Here's your caption</MobileWizard.Title>
          </MobileWizard.Section>
          <MobileWizard.Section gap={25}>
            <CaptionDisplay
              streaming={streaming}
              content={content}
              hasError={hasError}
              displayedCaption={displayedCaption}
              disabled={true}
            />
            <ActionButtons
              displayedCaption={displayedCaption}
              hasError={hasError}
              streaming={streaming}
              allOptions={allOptions}
              onCopy={handleCopy}
              onRegenerate={handleRegenerate}
              onOpenInWriter={handleOpenInWriter}
            />
            {allOptions.length > 1 && (
              <CaptionOptionsList
                options={allOptions.slice(0, 5)}
                selectedIndex={selectedCopyId}
                onSelect={setSelectedCopyId}
              />
            )}
          </MobileWizard.Section>
        </MobileWizard.Content>
      </>
    )
  }),
)
